import { BLOCKED_ANALYTICS_SCRIPT_PATTERNS, CHUNK_ERROR_PATTERNS } from '~/constants/datadog'
import type { ExperimentsMapped } from '#shared/types/experiment'

type RUMEvent = {
  error?: {
    message?: string
    stack?: string
  }
}

type UserRole = 'owner' | 'user'

const chunkErrorPatternsInline = CHUNK_ERROR_PATTERNS.map((pattern) => pattern.toString()).join(', ')
const blockedAnalyticsScriptPatternsInline = BLOCKED_ANALYTICS_SCRIPT_PATTERNS.map((pattern) => pattern.toString()).join(', ')

const beforeSend = new Function('event', `
    if (event.error?.message && [${chunkErrorPatternsInline}].some(function (pattern) {
      return pattern.test(event.error.message)
    })) {
      return false
    }

    if (event.error?.message || event.error?.stack) {
      var analyticsHaystack = (event.error.message || '') + (event.error.stack || '')
      if ([${blockedAnalyticsScriptPatternsInline}].some(function (pattern) {
        return pattern.test(analyticsHaystack)
      })) {
        return false
      }
    }

    if (event.error?.message) {
      var regex = /Object Not Found Matching Id:\\d+,\\sMethodName:\\w+,\\sParamCount:\\d+/ig
      if (regex.test(event.error.message)) return false
    }
    return true
  `) as (event: RUMEvent) => boolean

/**
 * Injects the Datadog script into the head.
 *
 * __dangerouslyDisableSanitizersByTagID is required to tell Nuxt to not escape
 * characters in this block.
 *
 * @param isEnabled Whether Datadog is enabled.
 * @param isNotSupported Whether Datadog is not supported.
 * @param sampleRate The sample rate for Datadog.
 * @param rtSessionId The RT session ID.
 * @param $config The configuration for Datadog.
 * @param userRole The user role.
 * @param experiments The experiments.
 * @returns The Datadog script object.
 */
export function injectDatadog({
  isEnabled,
  isNotSupported,
  sampleRate,
  rtSessionId,
  $config,
  userRole,
  experiments,
}: {
  isEnabled: boolean
  isNotSupported: boolean
  sampleRate: number
  rtSessionId: string
  $config: Record<string, unknown>
  userRole: UserRole
  experiments: ExperimentsMapped
}) {
  if (!isEnabled) {
    return
  }

  return {
    key: 'datadog',
    type: 'text/javascript' as const,
    innerHTML: injectDatadogScript({ isNotSupported, sampleRate, rtSessionId, $config, userRole, experiments }),
  }
}

/**
 * Generates and returns the actual script content for DataDog to be injected.
 *
 * @param isNotSupported Whether Datadog is not supported.
 * @param sampleRate The sample rate for Datadog.
 * @param rtSessionId The RT session ID.
 * @param $config The configuration for Datadog.
 * @param userRole The user role.
 * @param experiments The experiments.
 * @returns The Datadog script content.
 */
function injectDatadogScript({
  isNotSupported,
  sampleRate,
  rtSessionId,
  $config,
  userRole,
  experiments,
}: {
  isNotSupported: boolean
  sampleRate: number
  rtSessionId: string
  $config: Record<string, unknown>
  userRole: UserRole
  experiments: ExperimentsMapped
}): string {
  if (isNotSupported) {
    return ''
  }

  /* v8 ignore start */
  const datadogConfig = {
    ...$config,
    trackUserInteractions: true,
    sessionSampleRate: sampleRate,
    beforeSend,
  }
  /* v8 ignore end */

  return `
  (function(h,o,u,n,d) {
    h=h[d]=h[d]||{q:[],onReady:function(c){h.q.push(c)}}
    d=o.createElement(u);d.async=1;d.src=n
    n=o.getElementsByTagName(u)[0];n.parentNode.insertBefore(d,n)
  })(window,document,'script','https://www.datadoghq-browser-agent.com/us1/v5/datadog-rum-slim.js','DD_RUM');
  const originalWarn = window.console.warn;
  window.console.warn = (...args) => {
    if (args[0] && typeof args[0] === 'string' && args[0].startsWith('[Vue warn]')) {
      const safeMessage = args.map(String).join('')
      window?.DD_RUM?.addError(new Error(safeMessage))
    }
    originalWarn(...args)
  };
  window.DD_RUM.onReady(function() {
    var config = ${JSON.stringify($config).replace(/"[*]regex(\/[^/]+?\/[gims]*)"/g, '$1')}
    config.trackUserInteractions = ${datadogConfig.trackUserInteractions};
    config.sessionSampleRate = ${datadogConfig.sessionSampleRate};
    config.beforeSend = ${datadogConfig.beforeSend.toString()};
    window.DD_RUM.setGlobalContextProperty("rtsessionId", "${rtSessionId}");
    window.DD_RUM.setGlobalContextProperty("role", "${userRole}");
    window.DD_RUM.setGlobalContextProperty("experiments", '${JSON.stringify(experiments)}');
    window.DD_RUM.init(config);
  })
  `
}
